/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 24/05/22
 */

import React from 'react';
import { ArrayLayoutProps, isLayout, UISchemaElement } from '@jsonforms/core';
import { JsonFormsDispatch } from '@jsonforms/react';
import { Box, Flex, IconButton, Tooltip } from '@chakra-ui/react';
import Icons from '@/components/icons/Icons';
import { namedMemo } from '@/utils/namedMemo';

interface ChakraListWithDetailItemProps
    extends Pick<ArrayLayoutProps, 'uischema' | 'renderers' | 'cells' | 'visible' | 'schema'> {
    childPath: string;
    removeItem?(): void;
    isReadonly?: boolean;
    autoFocus?: boolean;
    hideListControl?: boolean;
}

const ChakraListWithDetailItem = namedMemo(
    'ChakraListWithDetailItem',
    ({
        removeItem,
        renderers,
        cells,
        childPath,
        visible,
        schema,
        uischema,
        isReadonly,
        autoFocus,
        hideListControl,
    }: ChakraListWithDetailItemProps) => {
        if (autoFocus) {
            let elementUIschema: UISchemaElement | null = null;

            if (isLayout(uischema) && uischema.elements.length > 0) elementUIschema = uischema.elements[0];
            else elementUIschema = uischema;

            if (elementUIschema) {
                elementUIschema.options = { ...(elementUIschema.options ?? {}), focus: true };
            }
        }

        return (
            <Box key={childPath} w={'100%'} p={3} px={6} borderBottom={'1px solid'} borderColor={'gray.200'}>
                <Flex flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} gridGap={4}>
                    <JsonFormsDispatch
                        renderers={renderers}
                        cells={cells}
                        visible={visible}
                        schema={schema}
                        uischema={uischema}
                        path={childPath}
                    />
                    {!isReadonly && !hideListControl && (
                        <Tooltip label="Delete">
                            <IconButton
                                colorScheme="red"
                                aria-label="Delete"
                                variant="outline"
                                icon={<Icons.CloseIcon w={8} h={8} />}
                                onClick={removeItem}
                            />
                        </Tooltip>
                    )}
                </Flex>
            </Box>
        );
    },
);

export default ChakraListWithDetailItem;
